/**
 * RefereeCardStatsCard - Referee card tendencies and strictness
 *
 * Uses the same styling pattern as TeamsTab for consistency.
 */
import React from 'react'
import type { RefereeCardContext, TeamLite } from '../types'
import { Sparkline } from './Sparkline'

interface RefereeCardStatsCardProps {
  referee: RefereeCardContext | null
  homeTeam: TeamLite
  awayTeam: TeamLite
}

function StrictnessBadge({ label, strictness }: { label: string; strictness: number }) {
  const color =
    label === 'strict' ? 'var(--red)' :
    label === 'lenient' ? 'var(--green)' :
    'var(--muted)'

  const bgColor =
    label === 'strict' ? 'rgba(239, 68, 68, 0.15)' :
    label === 'lenient' ? 'rgba(74, 222, 128, 0.15)' :
    'rgba(139, 139, 158, 0.15)'

  return (
    <span
      style={{
        color,
        background: bgColor,
        padding: '3px 8px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {label} ({strictness.toFixed(2)}x)
    </span>
  )
}

export function RefereeCardStatsCard({
  referee,
  homeTeam,
  awayTeam,
}: RefereeCardStatsCardProps) {
  if (!referee) {
    return (
      <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
        <div className="card-inner">
          <div className="small" style={{ textAlign: 'center', color: 'var(--muted)', padding: '20px 0' }}>
            Referee not yet assigned
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
      <div className="card-inner">
        {/* Header with referee name and strictness */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <strong style={{ fontSize: 14 }}>{referee.name}</strong>
            <div className="small">{referee.matches} matches this season</div>
          </div>
          <StrictnessBadge label={referee.strictness_label} strictness={referee.strictness} />
        </div>

        {/* Stats grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
          <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(255,255,255,.03)', borderRadius: 6 }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--yellow)' }}>{referee.yellows_per_90.toFixed(1)}</div>
            <div className="small">Yellows/game</div>
          </div>
          <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(255,255,255,.03)', borderRadius: 6 }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--red)' }}>{referee.reds_per_90.toFixed(2)}</div>
            <div className="small">Reds/game</div>
          </div>
          <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(255,255,255,.03)', borderRadius: 6 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{referee.fouls_per_card.toFixed(1)}</div>
            <div className="small">Fouls/card</div>
          </div>
        </div>

        {/* Recent cards sparkline */}
        {referee.recent_cards.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '8px 0', borderTop: '1px solid var(--border)' }}>
            <span className="small">Recent matches</span>
            <Sparkline
              values={referee.recent_cards}
              height={24}
              width={70}
              color="var(--yellow)"
            />
          </div>
        )}

        {/* Team-specific tendencies */}
        {(referee.home_team_tendency || referee.away_team_tendency) && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <div className="small" style={{ marginBottom: 8 }}>Team Tendencies</div>

            {referee.home_team_tendency && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', fontSize: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {homeTeam.crest_url && <img src={homeTeam.crest_url} alt="" style={{ width: 16, height: 16 }} />}
                  <span>{homeTeam.short_name || homeTeam.name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{referee.home_team_tendency.yellows_per_90?.toFixed(1) ?? '-'} cards/g</span>
                  {referee.home_team_tendency.yellows_deviation !== null && Math.abs(referee.home_team_tendency.yellows_deviation) >= 0.1 && (
                    <span style={{ color: referee.home_team_tendency.yellows_deviation > 0 ? 'var(--red)' : 'var(--green)' }}>
                      {referee.home_team_tendency.yellows_deviation > 0 ? '+' : ''}{referee.home_team_tendency.yellows_deviation.toFixed(1)}
                    </span>
                  )}
                  <span className="small">({referee.home_team_tendency.matches})</span>
                </div>
              </div>
            )}

            {referee.away_team_tendency && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', fontSize: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {awayTeam.crest_url && <img src={awayTeam.crest_url} alt="" style={{ width: 16, height: 16 }} />}
                  <span>{awayTeam.short_name || awayTeam.name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{referee.away_team_tendency.yellows_per_90?.toFixed(1) ?? '-'} cards/g</span>
                  {referee.away_team_tendency.yellows_deviation !== null && Math.abs(referee.away_team_tendency.yellows_deviation) >= 0.1 && (
                    <span style={{ color: referee.away_team_tendency.yellows_deviation > 0 ? 'var(--red)' : 'var(--green)' }}>
                      {referee.away_team_tendency.yellows_deviation > 0 ? '+' : ''}{referee.away_team_tendency.yellows_deviation.toFixed(1)}
                    </span>
                  )}
                  <span className="small">({referee.away_team_tendency.matches})</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
